import { useState, useEffect } from "react";
import axios from "axios";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  ShoppingCart,
  Receipt,
  Percent,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_COLORS = ["#D97706", "#F59E0B", "#10B981", "#3B82F6", "#8B5CF6"];

const PRODUCT_LABELS = {
  donuts: "Donuts",
  meat_pies: "Meat Pies",
  sausage_rolls: "Sausage Rolls",
  hotdog_rolls: "Hotdog Rolls",
  egg_rolls: "Egg Rolls"
};

const COST_LABELS = {
  staff_salaries: "Staff Salaries",
  raw_materials: "Raw Materials",
  utilities: "Utilities",
  fuel: "Fuel",
  vehicle_maintenance: "Vehicle Maintenance",
  miscellaneous: "Miscellaneous"
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

export default function Dashboard() {
  const [period, setPeriod] = useState("monthly");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, [period]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/dashboard?period=${period}`);
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const salesByProductData = dashboardData?.sales_by_product 
    ? Object.entries(dashboardData.sales_by_product).map(([key, value]) => ({
        name: PRODUCT_LABELS[key] || key,
        value: value.amount,
        quantity: value.quantity
      }))
    : [];

  const costsByCategoryData = dashboardData?.costs_by_category
    ? Object.entries(dashboardData.costs_by_category).map(([key, value]) => ({
        name: COST_LABELS[key] || key,
        value: value
      }))
    : [];

  const dailyChartData = dashboardData?.daily_data || [];

  if (loading) {
    return (
      <div className="space-y-8 animate-fade-in" data-testid="dashboard-loading">
        <div className="h-8 w-48 skeleton" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1,2,3,4].map(i => (
            <div key={i} className="h-32 skeleton rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const isProfit = (dashboardData?.total_profit || 0) >= 0;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 tracking-tight">Dashboard</h1>
          <p className="text-stone-500 mt-1">Track your bakery's performance</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40" data-testid="period-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">Daily</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="quarterly">Quarterly</SelectItem>
            <SelectItem value="yearly">Yearly</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="card-hover animate-slide-up stagger-1" data-testid="stat-sales">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-stone-500 uppercase tracking-wide">Total Sales</p>
                <p className="text-2xl font-bold text-stone-900 mt-2">
                  {formatCurrency(dashboardData?.total_sales || 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover animate-slide-up stagger-2" data-testid="stat-costs">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-stone-500 uppercase tracking-wide">Total Costs</p>
                <p className="text-2xl font-bold text-stone-900 mt-2">
                  {formatCurrency(dashboardData?.total_costs || 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <Receipt className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`card-hover animate-slide-up stagger-3 ${isProfit ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`} data-testid="stat-profit">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-stone-500 uppercase tracking-wide">Net Profit</p>
                <p className={`text-2xl font-bold mt-2 ${isProfit ? 'text-emerald-700' : 'text-red-700'}`}>
                  {formatCurrency(dashboardData?.total_profit || 0)}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isProfit ? 'bg-emerald-100' : 'bg-red-100'}`}>
                {isProfit ? (
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-red-600" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover animate-slide-up stagger-4" data-testid="stat-margin">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-stone-500 uppercase tracking-wide">Profit Margin</p>
                <p className="text-2xl font-bold text-stone-900 mt-2">
                  {dashboardData?.profit_margin?.toFixed(1) || 0}%
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Percent className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Trend Chart */}
        <Card className="animate-slide-up stagger-2" data-testid="chart-daily-trend">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-stone-800">Daily Trends</CardTitle>
          </CardHeader>
          <CardContent>
            {dailyChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#78716C"
                    fontSize={12}
                    tickFormatter={(val) => val.slice(5)}
                  />
                  <YAxis stroke="#78716C" fontSize={12} />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E7E5E4',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="sales" 
                    stroke="#D97706" 
                    strokeWidth={2}
                    dot={{ fill: '#D97706' }}
                    name="Sales"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="costs" 
                    stroke="#DC2626" 
                    strokeWidth={2}
                    dot={{ fill: '#DC2626' }}
                    name="Costs"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="profit" 
                    stroke="#10B981" 
                    strokeWidth={2}
                    dot={{ fill: '#10B981' }}
                    name="Profit"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state h-[300px]">
                <p>No data available for this period</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sales by Product */}
        <Card className="animate-slide-up stagger-3" data-testid="chart-sales-by-product">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-stone-800">Sales by Product</CardTitle>
          </CardHeader>
          <CardContent>
            {salesByProductData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={salesByProductData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {salesByProductData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E7E5E4',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state h-[300px]">
                <p>No sales data available</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Costs Breakdown */}
      <Card className="animate-slide-up stagger-4" data-testid="chart-costs-breakdown">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-stone-800">Costs Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          {costsByCategoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={costsByCategoryData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                <XAxis type="number" stroke="#78716C" fontSize={12} />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  stroke="#78716C" 
                  fontSize={12}
                  width={120}
                />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #E7E5E4',
                    borderRadius: '8px'
                  }}
                />
                <Bar 
                  dataKey="value" 
                  fill="#D97706" 
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state h-[300px]">
              <p>No cost data available</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
